const baseUrl = import.meta.env.VITE_REACT_APP_SERVER;

interface apiRequestInterface {
  subRoute?: string;
  path: string;
  method?: string;
  body?: any;
  formData?: FormData;
  responseType?: string;
  additionalHeaders?: { [key: string]: string };
}

export async function apiRequest<T>({
  subRoute,
  path,
  method = "GET",
  body,
  formData,
  responseType = "json",
  additionalHeaders = {}
}: apiRequestInterface): Promise<T> {
  const baseRoute = subRoute === undefined ? baseUrl : `${baseUrl}/${subRoute}`;

  const url = `${baseRoute}${path}`;
  interface RequestPayload {
    method: string;
    credentials: RequestCredentials;
    headers: { [key: string]: string };
    payload?: Record<string, any>;
    body?: string | FormData;
  }

  if (formData && body) {
    throw new Error("Invalid params, can't have form data and body");
  }

  const payload: RequestPayload = {
    method: method,
    credentials: "include",
    headers: additionalHeaders
  };

  if (body) {
    payload.body = JSON.stringify(body);
    payload.headers = {
      ...payload.headers,
      "Content-type": "application/json"
    };
  }

  if (formData) {
    payload.body = formData;
  }

  const response = await fetch(url, payload);

  if (response.status === 401 || response.status === 403) {
    // The auth_user_token cookie expired or got rejected. Don't nuke
    // all cookies / all localStorage — that wipes Auth0 SDK state
    // and forces a full re-authentication (including consent prompt
    // on every random 401). Instead, expire just the auth cookie and
    // let the React auth flow refresh the token on the next mount.
    document.cookie =
      "auth0_user_token=; expires=" +
      new Date(0).toUTCString() +
      "; path=/";
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }
    return null;
  }

  if (response.status === 429) {
    // Spending limit exceeded - dispatch custom event
    const errorData = await response.json();
    window.dispatchEvent(
      new CustomEvent("spending-limit-exceeded", {
        detail: errorData
      })
    );
    throw new Error(errorData.detail || "Monthly spending limit exceeded");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`
    );
  }

  if (responseType === "text") {
    return (await response.text()) as T;
  } else if (responseType === "stream") {
    return response.body as T;
  } else if (responseType === "blob") {
    return response.blob() as T;
  }

  const data = await response.json();

  return data as T;
}

// ── Studio Podcasts ──────────────────────────────────────────────────────────

import type {
  Podcast,
  PodcastAvailableSources,
  PodcastDataSource,
  SharedPodcast,
} from "./types/dbTypes";

export interface CreatePodcastPayload {
  title: string;
  data_sources: PodcastDataSource[];
  podcast_type: Podcast["podcast_type"];
  podcast_length: Podcast["podcast_length"];
  podcast_tone: Podcast["podcast_tone"];
  custom_prompt?: string;
}

export const listPodcasts = () =>
  apiRequest<Podcast[]>({ path: "/podcast/list" });

export const getPodcastAvailableSources = () =>
  apiRequest<PodcastAvailableSources>({ path: "/podcast/data_sources" });

export const createPodcast = (body: CreatePodcastPayload) =>
  apiRequest<Podcast>({ path: "/podcast/create", method: "POST", body });

export const getPodcast = (id: string) =>
  apiRequest<Podcast>({ path: `/podcast/${id}` });

export const deletePodcast = (id: string) =>
  apiRequest<{ message: string }>({ path: `/podcast/${id}`, method: "DELETE" });

export const regeneratePodcast = (id: string, body?: Partial<CreatePodcastPayload>) =>
  apiRequest<{ message: string; podcast_id: string }>({
    path: `/podcast/${id}/regenerate`,
    method: "POST",
    body,
  });

export const sharePodcast = (id: string) =>
  apiRequest<{ token: string; share_url: string }>({
    path: `/podcast/${id}/share`,
    method: "POST",
  });

export const revokePodcastShare = (id: string) =>
  apiRequest<{ message: string }>({
    path: `/podcast/${id}/share`,
    method: "DELETE",
  });

export const getSharedPodcast = (token: string) =>
  apiRequest<SharedPodcast>({ path: `/podcast/share/${token}` });
